import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Step,
  StepConnector,
  stepConnectorClasses,
  StepLabel,
  Stepper,
  styled,
} from '@mui/material';
import { StepIconProps } from '@mui/material/StepIcon';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Iconify from 'src/components/iconify';
import { campaignApi } from '../../../../api/campaign';
import { PATH_DASHBOARD } from '../../../../routes/paths';
import { CampaignFormValues } from '../CreateCampaignWizard';
import { useCreateCampaign } from '../hooks/useCampaigns';
import CampaignStep1Basic from './CampaignStep1Basic';
import CampaignStep2Budget from './CampaignStep2Budget';
import CampaignStep3Summary from './CampaignStep3Summary';
import { translateKey } from 'src/locales/translate';


const STEPS = ['Informacje', translateKey("hardcoded.budzet"), translateKey("quickBooking.payment")];

const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 3,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: theme.palette.primary.main,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: theme.palette.primary.main,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 2,
    border: 0,
    backgroundColor: theme.palette.divider,
  },
}));

const ColorlibStepIconRoot = styled('div')<{
  ownerState: { completed?: boolean; active?: boolean };
}>(({ theme, ownerState }) => ({
  zIndex: 1,
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: theme.palette.action.disabledBackground,
  ...(ownerState.active && {
    backgroundColor: theme.palette.primary.main,
  }),
  ...(ownerState.completed && {
    backgroundColor: 'transparent',
  }),
}));

function ColorlibStepIcon({
  active,
  completed,
  className,
}: StepIconProps): JSX.Element {
  return (
    <ColorlibStepIconRoot
      ownerState={{ completed, active }}
      className={className}
    >
      {completed && (
        <Iconify
          color="#50CEFF"
          icon="ic:round-check"
          sx={{ position: 'relative', top: -9, left: -5 }}
          width={24}
        />
      )}
    </ColorlibStepIconRoot>
  );
}

const DEFAULT_FORM_VALUES: CampaignFormValues = {
  name: '',
  description: '',
  keywords: [],
  dailyBudget: 50,
  startDate: null,
  endDate: null,
};

interface CreateCampaignModalProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  onCreated: () => void;
}

export default function CreateCampaignModal({
  open,
  onClose,
  organizationId,
  onCreated,
}: CreateCampaignModalProps): JSX.Element {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [activeStep, setActiveStep] = useState(0);
  const [formValues, setFormValues] =
    useState<CampaignFormValues>(DEFAULT_FORM_VALUES);
  const [isCreating, setIsCreating] = useState(false);

  const createCampaignMutation = useCreateCampaign(organizationId);

  const handleClose = (): void => {
    onClose();
    setTimeout(() => {
      setActiveStep(0);
      setFormValues(DEFAULT_FORM_VALUES);
    }, 300);
  };

  const handleBack = (): void => {
    setActiveStep((prev) => prev - 1);
  };

  const handleStep1Complete = (values: {
    name: string;
    description: string;
    keywords: string[];
  }): void => {
    setFormValues((prev) => ({ ...prev, ...values }));
    setActiveStep(1);
  };

  const handleStep2Complete = (values: {
    dailyBudget: number;
    startDate: Date;
    endDate: Date;
  }): void => {
    setFormValues((prev) => ({ ...prev, ...values }));
    setActiveStep(2);
  };

  const handlePayment = async (promoCode?: string): Promise<void> => {
    setIsCreating(true);
    try {
      const campaign = await createCampaignMutation.mutateAsync({
        name: formValues.name,
        description: formValues.description,
        keywords: formValues.keywords,
        dailyBudget: formValues.dailyBudget,
        startDate: formValues.startDate?.toISOString().split('T')[0],
        endDate: formValues.endDate?.toISOString().split('T')[0],
        promoCode: promoCode || undefined,
      });

      const paymentResponse =
        await campaignApi.campaignControllerInitiatePayment({
          campaignId: campaign.id,
        });

      if (paymentResponse.data.paymentUrl) {
        enqueueSnackbar(translateKey("hardcoded.przekierowanie_do_platnosci"), { variant: 'info' });
        window.location.replace(paymentResponse.data.paymentUrl);
      } else {
        enqueueSnackbar(translateKey("hardcoded.kampania_zostala_aktywowana"), { variant: 'success' });
        onCreated();
        handleClose();
        navigate(
          PATH_DASHBOARD.general.organizations.campaigns(organizationId),
        );
      }
    } catch (error: any) {
      enqueueSnackbar(error.message || translateKey("hardcoded.blad_podczas_tworzenia_kampanii"), {
        variant: 'error',
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: 'calc(100vw - 32px)', sm: 'calc(100vw - 48px)' },
          maxWidth: '800px',
          mx: 0,
          my: { xs: 2, sm: 4 },
        },
      }}
    >
      <Stack
        sx={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 3,
        }}
      >
        <DialogTitle sx={{ p: 0 }}>Nowa kampania Google Ads</DialogTitle>
        <IconButton onClick={handleClose}>
          <Iconify icon="eva:close-fill" />
        </IconButton>
      </Stack>

      <DialogContent sx={{ pb: 3 }}>
        <Stepper
          alternativeLabel
          activeStep={activeStep}
          connector={<ColorlibConnector />}
          sx={{ mt: 1, mb: 3, width: '100%' }}
        >
          {STEPS.map((label, index) => (
            <Step key={label} completed={index < activeStep}>
              <StepLabel
                slots={{
                  stepIcon: (props) => <ColorlibStepIcon {...props} />,
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <CampaignStep1Basic
            initialValues={{
              name: formValues.name,
              description: formValues.description,
              keywords: formValues.keywords,
            }}
            organizationId={organizationId}
            onNext={handleStep1Complete}
            onCancel={handleClose}
            isLoading={createCampaignMutation.isPending}
          />
        )}

        {activeStep === 1 && (
          <CampaignStep2Budget
            initialValues={{
              dailyBudget: formValues.dailyBudget,
              startDate: formValues.startDate,
              endDate: formValues.endDate,
            }}
            onNext={handleStep2Complete}
            onBack={handleBack}
            isLoading={false}
          />
        )}

        {activeStep === 2 && (
          <CampaignStep3Summary
            formValues={formValues}
            onPayment={handlePayment}
            onBack={handleBack}
            isLoading={isCreating}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
